import { MilestonePackageActions } from '@/types/Milestone';
import { Origin } from '@/types/ProductionValueCheck';
import { Badge, Box, Button } from '@palmetto/palmetto-components';
import { useFormikContext } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';

import {
  DomesticContentAttestmentType,
  ITCQualificationResponse,
  ProgramType,
  RequirementFlagSettingsType,
  UserPermissions,
} from 'types';

import RequirePalmettoFinanceUser from '../auth/RequirePalmettoFinanceUser';
import RequirePermissions from '../auth/requirePermissions';
import FormSection from '../Forms/FormSection';
import { SystemDetailsAndSiteQualificationSectionDescription } from '../InstallMilestonePackage/InstallationFormSectionDescriptions';
import { FlagBox } from '../QueueFlags/FlagBox';
import { EquipmentListSection } from '../SystemDesign/EquipmentSection';
import { PVEquipment } from '../SystemDesign/PVEquipment';

interface PVSystemProps {
  programType: ProgramType;
  showUpdateSystemButton?: boolean;
  attestmentType: DomesticContentAttestmentType;
  readOnly?: boolean;
  itcQualificationResult?: ITCQualificationResponse;
  isItcSystemDesignCompleted?: boolean;
  hasFeocException?: boolean;
  orgAlias?: string;
  isPackageEditable?: boolean;
}

export const PVSystemFormSection = ({
  programType,
  attestmentType,
  readOnly,
  showUpdateSystemButton = false,
  itcQualificationResult,
  isItcSystemDesignCompleted = false,
  hasFeocException = false,
  orgAlias,
  isPackageEditable = true,
}: PVSystemProps) => {
  const navigate = useNavigate();
  const { id, action } = useParams<{ id: any; action: any }>();
  const { values } = useFormikContext<any>();

  return (
    <FormSection
      title="System Details & Site Qualification"
      description={SystemDetailsAndSiteQualificationSectionDescription()}
      {...((showUpdateSystemButton || itcQualificationResult) && {
        headerChildren: (
          <>
            {showUpdateSystemButton && (
              <>
                <Box>
                  Need to update the System Design to change FEOC or Domestic Content values? Click the button to
                  update the System Design and re-calculate.
                </Box>
                <Button
                  onClick={async () => {
                    navigate(`/accounts/${id}/itc-system-design`, {
                      state: {
                        from: Origin.INSTALL_PACKAGE,
                        action: action || MilestonePackageActions.create,
                      },
                    });
                  }}
                  size="sm"
                  variant="secondary"
                >
                  Update System
                </Button>
              </>
            )}
            {itcQualificationResult && (
              <Box gap="xs" margin={showUpdateSystemButton ? 'md 0 0 0' : undefined}>
                {itcQualificationResult.pvFeocQualified && (
                  <Box>
                    <span>
                      <strong>non-FEOC Value (PV):</strong>{' '}
                      {(itcQualificationResult.pvTotalFeocPercentage ?? 0).toFixed(2)}%
                    </span>
                  </Box>
                )}
                {itcQualificationResult.batteryFeocQualified && (
                  <Box>
                    <span>
                      <strong>non-FEOC Value (Storage):</strong>{' '}
                      {(itcQualificationResult.batteryFeocPercentage ?? 0).toFixed(2)}%
                    </span>
                  </Box>
                )}
                <Box>
                  <span>
                    <strong>non-FEOC Threshold:</strong> {itcQualificationResult.pvSystemFeocPercentageThreshold}% PV
                    / {itcQualificationResult.batteryFeocPercentageThreshold}% Storage
                  </span>
                </Box>
                {hasFeocException && (
                  <Box direction="row" childGap="xs">
                    <Badge variant="warning" message="Exception Granted" />
                  </Box>
                )}
              </Box>
            )}
          </>
        ),
      })}
    >
      <RequirePalmettoFinanceUser>
        <RequirePermissions permissions={[UserPermissions.lightReachQCTech]}>
          <EquipmentListSection installationData={values} />
        </RequirePermissions>
      </RequirePalmettoFinanceUser>
      {programType && (
        <PVEquipment
          programType={programType}
          attestmentType={attestmentType}
          readOnly={readOnly}
          isItcSystemDesignCompleted={isItcSystemDesignCompleted}
          orgAlias={orgAlias}
          isPackageEditable={isPackageEditable}
          useSearchableModelSelect
        />
      )}
      <FlagBox dataKey="PV System" flagType={RequirementFlagSettingsType.photoVoltaicSystem} />
      <Box margin={'lg 0 0 0'}>
        <FlagBox dataKey="Site Eligibility" flagType={RequirementFlagSettingsType.siteEligibility} />
      </Box>
    </FormSection>
  );
};
